import type { StreamPart } from "../types";

type RawPart = StreamPart & { id?: string };

/**
 * Stateful SSE reader for the AI-SDK UI-message stream. Returns a `feed`
 * function: call it with each Uint8Array chunk; it invokes `onPart` for every
 * complete `data-*` event. Buffers partial trailing events between calls.
 */
export const createSSEParser = (onPart: (part: RawPart) => void) => {
	const decoder = new TextDecoder("utf-8");
	let buffer = "";

	const flushEvent = (block: string) => {
		const payload = block
			.split("\n")
			.filter((line) => line.startsWith("data:"))
			.map((line) => line.slice(5).trim())
			.join("");
		if (!payload || payload === "[DONE]") {
			return;
		}
		let parsed: RawPart;
		try {
			parsed = JSON.parse(payload) as RawPart;
		} catch {
			return;
		}
		if (typeof parsed?.type === "string" && parsed.type.startsWith("data-")) {
			onPart(parsed);
		}
	};

	return (chunk: Uint8Array) => {
		buffer += decoder.decode(chunk, { stream: true });
		const blocks = buffer.split("\n\n");
		buffer = blocks.pop() ?? "";
		for (const block of blocks) {
			flushEvent(block);
		}
	};
};

/**
 * Drains a streamed response body, handing each chunk to `feed` (a parser from
 * createSSEParser) until the stream ends. Shared by the turn + summative calls.
 */
export const pumpSSEStream = async (
	body: ReadableStream<Uint8Array> | null | undefined,
	feed: (chunk: Uint8Array) => void,
) => {
	const reader = body?.getReader();
	if (!reader) {
		throw new Error("no stream");
	}
	for (;;) {
		const { done, value } = await reader.read();
		if (done) {
			break;
		}
		if (value) {
			feed(value);
		}
	}
};
